import type { HireRecommendationsSelection } from "~/lib/marketplace/hire-recommendations-pricing";
import {
  INCLUDED_FREE_PHONE_MINUTES,
  INCLUDED_FREE_SMS,
} from "~/lib/marketplace/hire-recommendations-pricing";
import type { IndustryVertical } from "~/lib/utils/industry-profile";
import {
  HIRE_ONBOARDING_INITIAL_PROMPT_KEY,
} from "~/lib/marketplace/hire-onboarding-session";

export const HIRE_BUILDER_ONBOARDING_COMPLETE_KEY = "egpt-hire-builder-onboarding-complete";
export const HIRE_ONBOARDING_GOALS_KEY = "egpt-hire-onboarding-goals";
export const HIRE_BUILDER_ONBOARDING_STATE_KEY = "egpt-hire-builder-onboarding-state";

export const HIRE_ONBOARDING_WELCOME_MESSAGE =
  "Welcome to Inbound GPT. Let's deploy a custom AI teammate that saves time, cuts costs, and grows revenue — working alongside your staff, not replacing them.";

export const HIRE_ONBOARDING_WEBSITE_PROMPT =
  "I'd be happy to analyze your website and recommend ways an Inbound GPT agent can help you connect callers, visitors, and direct messages to the right person at your company. To get started, what is your website URL?";

export const HIRE_ONBOARDING_GOALS_PROMPT =
  "Based on your website, how can a custom Inbound GPT agent best help your team?";

export const HIRE_ONBOARDING_GOALS_PROMPT_GENERIC =
  "How can a custom Inbound GPT agent best help your team?";

export const HIRE_ONBOARDING_GOAL_OPTIONS = [
  "Capture more leads",
  "Book more appointments",
  "Answer FAQs instantly",
  "Cover after-hours inquiries",
  "Speed up follow-up",
  "Convert website traffic",
  "Onboard customers faster",
  "Reduce call volume",
] as const;

export type HireOnboardingGoal = (typeof HIRE_ONBOARDING_GOAL_OPTIONS)[number];

/** Tailored onboarding goal chips from website vertical + page content. */
export function buildOnboardingGoalsFromWebsite(
  vertical: IndustryVertical,
  contentText: string,
  _businessName?: string,
): string[] {
  const lower = contentText.toLowerCase();
  const picked: string[] = [];
  const add = (goal: string) => {
    if (picked.length >= 8) return;
    if (!picked.some((g) => g.toLowerCase() === goal.toLowerCase())) {
      picked.push(goal);
    }
  };

  const isEducation =
    vertical === "education" ||
    /\b(school|academy|university|college|admissions|enrollment|campus|tuition|jesuit|prep)\b/.test(
      lower,
    );
  const isDental =
    /\b(dental|dentist|orthodont|pediatric dent|kids smile|oral surgery|periodont|endodont)\b/.test(
      lower,
    );
  const isHealthcare =
    vertical === "healthcare" ||
    isDental ||
    /\b(clinic|patient|physician|therapy|chiropractic|medical|physical therapy)\b/.test(lower);
  const isLegal = vertical === "legal" || /\b(law firm|attorney|lawyer|legal)\b/.test(lower);
  const isHomeServices =
    vertical === "home_services" ||
    /\b(plumbing|hvac|roofing|contractor|home service)\b/.test(lower);

  if (isEducation) {
    add("Schedule campus tours");
    add("Answer admissions questions");
    add("Connect families with staff");
    if (/\b(financial aid|scholarship|tuition)\b/.test(lower)) {
      add("Explain financial aid options");
    }
    if (/\b(athletic|sports|varsity)\b/.test(lower)) {
      add("Support athletics inquiries");
    }
    if (/\b(alumni|giving|donat)\b/.test(lower)) {
      add("Connect alumni and donors");
    }
    add("Cover after-hours inquiries");
    add("Answer FAQs instantly");
  } else if (isDental) {
    add("Book more appointments");
    add("Never miss a call");
    add("Capture new patients");
    add("Convert new patients");
    add("Answer patient FAQs");
    add("Cover after-hours inquiries");
    if (/\b(insurance|billing)\b/.test(lower)) add("Help with insurance questions");
    add("Replace answering service");
  } else if (isHealthcare) {
    add("Book more appointments");
    add("Never miss a call");
    add("Capture new patients");
    add("Answer patient FAQs");
    add("Cover after-hours inquiries");
    if (/\b(insurance|billing)\b/.test(lower)) add("Help with insurance questions");
    add("Replace answering service");
    add("Reduce call volume");
  } else if (isLegal) {
    add("Capture intake leads");
    add("Schedule consultations");
    add("Answer common legal FAQs");
    add("Connect with attorneys");
    add("Cover after-hours inquiries");
    add("Never miss a call");
    add("Speed up follow-up");
  } else if (isHomeServices) {
    add("Book service appointments");
    add("Never miss a call");
    add("Capture emergency requests");
    add("Quote new projects");
    add("Answer service FAQs");
    add("Cover after-hours inquiries");
    add("Convert website traffic");
  } else {
    add("Capture more leads");
    add("Book more appointments");
    add("Never miss a call");
    add("Answer FAQs instantly");
    add("Connect visitors with staff");
  }

  for (const fallback of HIRE_ONBOARDING_GOAL_OPTIONS) {
    if (picked.length >= 8) break;
    add(fallback);
  }

  return picked.slice(0, 8);
}

export type HireBuilderOnboardingStep =
  | "welcome"
  | "website"
  | "analyzing"
  | "goals"
  | "setup"
  | "sign_up"
  | "complete";

export type SavedHireBuilderOnboardingState = {
  step: HireBuilderOnboardingStep;
  submittedWebsite?: string;
  selectedGoals?: string[];
};

function goalSelected(goals: readonly string[], label: string): boolean {
  return goals.some((g) => g.toLowerCase() === label.toLowerCase());
}

function goalMatchesAny(goals: readonly string[], labels: readonly string[]): boolean {
  return labels.some((label) => goalSelected(goals, label));
}

export function applyOnboardingGoalsToSelection(
  base: HireRecommendationsSelection,
  goals: readonly string[],
): HireRecommendationsSelection {
  if (goals.length === 0) return base;

  const captureLeads = goalMatchesAny(goals, [
    "Capture more leads",
    "Capture new patients",
    "Convert new patients",
    "Capture intake leads",
  ]);
  const bookAppointments = goalMatchesAny(goals, [
    "Book more appointments",
    "Book service appointments",
    "Schedule consultations",
    "Schedule campus tours",
  ]);
  const answerFaqs = goalMatchesAny(goals, [
    "Answer FAQs instantly",
    "Answer patient FAQs",
    "Answer service FAQs",
    "Answer common legal FAQs",
    "Answer admissions questions",
  ]);
  const afterHours = goalMatchesAny(goals, [
    "Cover after-hours inquiries",
    "Never miss a call",
    "Replace answering service",
  ]);
  const followUp = goalSelected(goals, "Speed up follow-up");
  const convertTraffic = goalSelected(goals, "Convert website traffic");
  const onboardCustomers = goalSelected(goals, "Onboard customers faster");
  const reduceCalls = goalSelected(goals, "Reduce call volume");

  return {
    ...base,
    liveChat:
      base.liveChat ||
      captureLeads ||
      answerFaqs ||
      afterHours ||
      convertTraffic ||
      onboardCustomers ||
      reduceCalls,
    textMessages:
      base.textMessages ||
      captureLeads ||
      followUp ||
      afterHours ||
      (base.smsCount > 0 ? true : captureLeads || followUp),
    email: base.email || followUp || onboardCustomers,
    browserCalls: base.browserCalls || convertTraffic || answerFaqs,
    phoneCalls:
      base.phoneCalls ||
      bookAppointments ||
      afterHours ||
      reduceCalls ||
      (base.phoneCallMinutes > 0 ? true : bookAppointments || afterHours),
    welcomeVideo: base.welcomeVideo || convertTraffic,
    appointmentsEnabled: base.appointmentsEnabled || bookAppointments,
    teamHandoffEnabled: base.teamHandoffEnabled ?? true,
    smsCount:
      base.smsCount > 0
        ? base.smsCount
        : captureLeads || followUp || afterHours
          ? INCLUDED_FREE_SMS
          : 0,
    phoneCallMinutes:
      base.phoneCallMinutes > 0
        ? base.phoneCallMinutes
        : bookAppointments || afterHours || reduceCalls
          ? INCLUDED_FREE_PHONE_MINUTES
          : 0,
  };
}

export function readHireBuilderOnboardingComplete(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      sessionStorage.getItem(HIRE_BUILDER_ONBOARDING_COMPLETE_KEY) === "1" ||
      localStorage.getItem(HIRE_BUILDER_ONBOARDING_COMPLETE_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function markHireBuilderOnboardingComplete(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(HIRE_BUILDER_ONBOARDING_COMPLETE_KEY, "1");
    localStorage.setItem(HIRE_BUILDER_ONBOARDING_COMPLETE_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function readSavedHireBuilderOnboardingState(): SavedHireBuilderOnboardingState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      sessionStorage.getItem(HIRE_BUILDER_ONBOARDING_STATE_KEY) ||
      localStorage.getItem(HIRE_BUILDER_ONBOARDING_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SavedHireBuilderOnboardingState;
  } catch {
    return null;
  }
}

export function writeSavedHireBuilderOnboardingState(state: SavedHireBuilderOnboardingState): void {
  if (typeof window === "undefined") return;
  try {
    const raw = JSON.stringify(state);
    sessionStorage.setItem(HIRE_BUILDER_ONBOARDING_STATE_KEY, raw);
    // Mirror to localStorage for email-verify / new-tab resume.
    localStorage.setItem(HIRE_BUILDER_ONBOARDING_STATE_KEY, raw);
  } catch {
    /* ignore */
  }
}

export function persistOnboardingGoals(goals: readonly string[]): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(HIRE_ONBOARDING_GOALS_KEY, JSON.stringify(goals));
    sessionStorage.setItem(
      HIRE_ONBOARDING_INITIAL_PROMPT_KEY,
      `My team wants help with: ${goals.join(", ")}. Please configure my Inbound GPT agent to support these goals.`,
    );
  } catch {
    /* ignore */
  }
}

export function readPersistedOnboardingGoals(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(HIRE_ONBOARDING_GOALS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((g): g is string => typeof g === "string") : [];
  } catch {
    return [];
  }
}

export function shouldSkipHireBuilderOnboarding(opts: {
  bootstrapComplete: boolean;
  previewGenerated?: boolean;
}): boolean {
  // Only skip once Preview exists. Do not skip on onboarding-complete alone —
  // Sign Up / email-verify resume still needs Trish until generate finishes.
  if (opts.previewGenerated) return true;
  return false;
}
